import type {
  ChannelTarget,
  DeliveryTarget,
  DmTarget,
  ParseError,
} from './types.js';

/** Parse an unknown JSON value into a `DeliveryTarget`.
 *
 *  Used at every ingress point — REST API validation, MCP tool validation,
 *  migration fixtures — so that no module downstream has to second-guess the
 *  wire shape. Error codes are precise so the UI can map them to clear
 *  rejection messages.
 *
 *  Rejects:
 *  - Non-object inputs (arrays, scalars, null when not allowed).
 *  - Unknown `kind` / `provider` / `medium` values.
 *  - Channel targets missing their required id.
 *  - DM targets missing `person_id`.
 *  - DM `medium` that's reserved (`teams`/`whatsapp`/`imessage`) but not yet
 *    dispatchable — rejected at save time per §6 so configured-but-broken
 *    schedules can't linger.
 *
 *  `follow_reports_to === true` paired with an arbitrary `person_id` is
 *  not caught here — that's a *contextual* invariant (depends on the
 *  agent's current reports_to) and belongs in API-layer validation.
 */
export function parseDeliveryTarget(
  raw: unknown,
): DeliveryTarget | ParseError {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      ok: false,
      code: 'MALFORMED_DELIVERY_TARGET',
      detail: 'delivery_to must be a JSON object',
    };
  }

  const obj = raw as Record<string, unknown>;
  const kind = obj['kind'];

  if (kind === 'channel') {
    return parseChannelTarget(obj);
  }
  if (kind === 'dm') {
    return parseDmTarget(obj);
  }

  return {
    ok: false,
    code: 'UNKNOWN_KIND',
    detail: `delivery_to.kind must be 'channel' or 'dm' (got ${JSON.stringify(kind)})`,
  };
}

function parseChannelTarget(
  obj: Record<string, unknown>,
): ChannelTarget | ParseError {
  const provider = obj['provider'];
  if (provider === 'slack') {
    const channelId = obj['channel_id'];
    if (typeof channelId !== 'string' || channelId.length === 0) {
      return {
        ok: false,
        code: 'MISSING_CHANNEL_ID',
        detail: "channel:slack target requires a non-empty channel_id",
      };
    }
    // ENG-6038: optional originating-thread coordinate. `null` is the wire
    // form of "top-level on purpose" — canonicalised here to an omitted
    // field, same as absent. Anything else non-string (or empty) is
    // malformed rather than silently dropped.
    const threadTs = obj['thread_ts'];
    if (threadTs !== undefined && threadTs !== null) {
      if (typeof threadTs !== 'string' || threadTs.length === 0) {
        return {
          ok: false,
          code: 'MALFORMED_DELIVERY_TARGET',
          detail: 'channel:slack thread_ts must be a non-empty string when present',
        };
      }
      return { kind: 'channel', provider: 'slack', channel_id: channelId, thread_ts: threadTs };
    }
    return { kind: 'channel', provider: 'slack', channel_id: channelId };
  }
  if (provider === 'telegram') {
    const chatId = obj['chat_id'];
    if (typeof chatId !== 'string' || chatId.length === 0) {
      return {
        ok: false,
        code: 'MISSING_CHAT_ID',
        detail: "channel:telegram target requires a non-empty chat_id",
      };
    }
    return { kind: 'channel', provider: 'telegram', chat_id: chatId };
  }
  return {
    ok: false,
    code: 'UNKNOWN_PROVIDER',
    detail: `channel.provider must be 'slack' or 'telegram' (got ${JSON.stringify(provider)})`,
  };
}

const SUPPORTED_MEDIUMS: ReadonlySet<string> = new Set(['auto', 'slack', 'telegram']);
const RESERVED_MEDIUMS: ReadonlySet<string> = new Set(['teams', 'whatsapp', 'imessage']);

function parseDmTarget(obj: Record<string, unknown>): DmTarget | ParseError {
  const personId = obj['person_id'];
  if (typeof personId !== 'string' || personId.length === 0) {
    return {
      ok: false,
      code: 'MISSING_PERSON_ID',
      detail: 'dm target requires a non-empty person_id',
    };
  }

  const followReportsTo = obj['follow_reports_to'];
  if (typeof followReportsTo !== 'boolean') {
    return {
      ok: false,
      code: 'MALFORMED_DELIVERY_TARGET',
      detail: 'dm.follow_reports_to must be a boolean',
    };
  }

  const medium = obj['medium'];
  if (typeof medium !== 'string') {
    return {
      ok: false,
      code: 'MALFORMED_DELIVERY_TARGET',
      detail: 'dm.medium must be a string',
    };
  }
  if (RESERVED_MEDIUMS.has(medium)) {
    return {
      ok: false,
      code: 'DM_MEDIUM_NOT_SUPPORTED',
      detail: `dm.medium '${medium}' is reserved but not yet dispatchable (ENG-4427)`,
    };
  }
  if (!SUPPORTED_MEDIUMS.has(medium)) {
    return {
      ok: false,
      code: 'UNKNOWN_MEDIUM',
      detail: `dm.medium must be 'auto', 'slack', or 'telegram' (got ${JSON.stringify(medium)})`,
    };
  }

  return {
    kind: 'dm',
    person_id: personId,
    follow_reports_to: followReportsTo,
    medium: medium as DmTarget['medium'],
  };
}

/** Narrow helper: true when the parse result is a parser error. */
export function isParseError(
  v: DeliveryTarget | ParseError,
): v is ParseError {
  return typeof v === 'object' && v !== null && 'ok' in v && v.ok === false;
}
